import { Injectable } from "@angular/core";

@Injectable({ providedIn: "root" })
export class DateUtilsService {
  normalizeToLocalDate(dateLike: Date | string): Date {
    const d = new Date(dateLike);
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    return new Date(year, month, day, 12, 0, 0, 0);
  }

  calculateReadyForInspectionDate(scheduledDate: Date): Date {
    const scheduled = this.normalizeToLocalDate(scheduledDate);
    const readyDate = new Date(scheduled);
    let businessDaysToSubtract = 2;
    while (businessDaysToSubtract > 0) {
      readyDate.setDate(readyDate.getDate() - 1);
      const dayOfWeek = readyDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDaysToSubtract = businessDaysToSubtract - 1;
      }
    }
    return readyDate;
  }

  businessDaysDiff(fromDate: Date, toDate: Date): number {
    const start = this.normalizeToLocalDate(fromDate);
    const end = this.normalizeToLocalDate(toDate);
    if (start.getTime() === end.getTime()) {
      return 0;
    }

    const step = start < end ? 1 : -1;
    const cursor = new Date(start);
    let days = 0;

    while (cursor.getTime() !== end.getTime()) {
      cursor.setDate(cursor.getDate() + step);
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) {
        days += step;
      }
    }

    return days;
  }

  addBusinessDays(startDate: Date, businessDays: number): Date {
    if (businessDays === 0) {
      return startDate;
    }

    const day = businessDays < 0 ? -1 : 1;
    const targetDays = Math.abs(businessDays);
    const current = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < targetDays) {
      current.setDate(current.getDate() + day);

      const sunday = 0;
      const saturday = 6;
      const dayOfTheWeek = current.getDay();
      if (dayOfTheWeek !== sunday && dayOfTheWeek !== saturday) {
        daysAdded++;
      }
    }

    return current;
  }

  /**
   * Converts a UTC date string or Date to a local date-only Date object.
   */
  convertUtcDateToLocalDateOnly(dateLike: string | Date): Date {
    const parsed = new Date(dateLike);
    const year = parsed.getUTCFullYear();
    const month = parsed.getUTCMonth();
    const day = parsed.getUTCDate();
    return new Date(year, month, day, 12, 0, 0, 0);
  }

  /**
   * Calculates the number of calendar days from today until the target date.
   * Returns 0 if the target date is in the past or today.
   */
  daysUntil(targetDate: Date | string): number {
    const today = this.normalizeToLocalDate(new Date());
    const target = this.normalizeToLocalDate(targetDate);
    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  /** Monday is the first day of the week everywhere in the app. */
  startOfWeek(dateLike: Date | string): Date {
    const start = this.normalizeToLocalDate(dateLike);
    const sunday = 0;
    const daysSinceMonday = start.getDay() === sunday ? 6 : start.getDay() - 1;
    start.setDate(start.getDate() - daysSinceMonday);

    return start;
  }

  /** Sunday of the calendar week containing the given date. */
  endOfWeek(dateLike: Date | string): Date {
    const end = this.startOfWeek(dateLike);
    end.setDate(end.getDate() + 6);

    return end;
  }

  addDays(dateLike: Date | string, days: number): Date {
    const result = this.normalizeToLocalDate(dateLike);
    result.setDate(result.getDate() + days);

    return result;
  }

  isDateThisWeekOrFuture(date: Date) {
    return this.normalizeToLocalDate(date) >= this.startOfWeek(new Date());
  }

  isDateThisWeek(date: Date) {
    const target = this.normalizeToLocalDate(date);

    return target >= this.startOfWeek(new Date()) && target <= this.endOfWeek(new Date());
  }

  isDateLastWeek(date: Date) {
    const startOfLastWeek = this.addDays(this.startOfWeek(new Date()), -7);
    const endOfLastWeek = this.addDays(startOfLastWeek, 6);
    const target = this.normalizeToLocalDate(date);

    return target >= startOfLastWeek && target <= endOfLastWeek;
  }

  toLocalISOString(date: Date): string {
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? "+" : "-";
    const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
      `${sign}${pad(offset / 60)}:${pad(offset % 60)}`;
  }

}


